import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUser, deleteUser } from "@/api/admin.api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ArrowLeft, Trash2 } from "lucide-react";

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "user", id],
    queryFn: () => getUser(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      navigate("/users");
    },
  });

  if (isLoading) {
    return (
      <div>
        <div className="mb-6 h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-muted-foreground">Kullanici bulunamadi</p>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/users")}>
          <ArrowLeft className="size-5" />
        </Button>
        <h1 className="text-2xl font-bold">{data.name}</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Kullanici Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              {data.avatar ? (
                <img src={data.avatar} alt="" className="size-16 rounded-full object-cover" />
              ) : (
                <div className="flex size-16 items-center justify-center rounded-full bg-muted text-xl font-medium">
                  {data.name[0]?.toUpperCase()}
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">E-posta</p>
              <p className="font-medium">{data.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Saglayici</p>
              <p className="font-medium capitalize">{data.provider}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Katilim Tarihi</p>
              <p className="font-medium">
                {new Date(data.createdAt).toLocaleDateString("tr-TR")}
              </p>
            </div>

            <ConfirmDialog
              trigger={
                <Button variant="destructive" className="mt-4 w-full gap-2">
                  <Trash2 className="size-4" />
                  Kullaniciyi Sil
                </Button>
              }
              title="Kullaniciyi Sil"
              description="Bu kullaniciyi ve tum verilerini silmek istediginizden emin misiniz? Bu islem geri alinamaz."
              confirmLabel="Sil"
              onConfirm={() => deleteMutation.mutate(id!)}
            />
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <h2 className="mb-4 text-lg font-semibold">Isletmeler ({data.venues.length})</h2>
          {data.venues.length === 0 ? (
            <p className="text-sm text-muted-foreground">Henuz isletme yok</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {data.venues.map((venue) => (
                <Card
                  key={venue._id}
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => navigate(`/venues/${venue._id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{venue.name}</p>
                        <p className="text-sm text-muted-foreground">/{venue.slug}</p>
                      </div>
                      <Badge variant="secondary">{venue.planType}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
